import { useEffect, useState } from 'react';
import { Modal, Button, Alert, Form, Tabs, Tab } from 'react-bootstrap';

import { noteFormRegistry } from '../customNoteForms';

import AppointmentTab from './AppointmentTab';
import ClinicalTab from './ClinicalTab';
import PrescriptionTab from './PrescriptionTab';
import ExtraTab from './ExtraTab';
import ImagePreviewModal from './ImagePreviewModal';

import { getAutosaveKey, saveToLocalStorage, loadFromLocalStorage, clearFromLocalStorage } from './autosave';
import { isDateInFuture, isDateToday } from './dateUtils';

import useOutsideClick from './useOutsideClick';
import useAttachments from './useAttachments';
import usePrescriptionData from './usePrescriptionData';
import useReferralsData from './useReferralsData';

const NoteModal = ({
    show,
    onHide,
    onSubmit,
    noteForm,
    onInputChange,
    submitting,
    submitError,
    editingNote,
    patientData = null
}) => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const isAssistant = userInfo.role === 'assistant';
    const doctorFieldId = (userInfo.doctor && userInfo.doctor.field_id) || userInfo.field_id || null;
    const isObgynDoctor = String(doctorFieldId || '') === '6';

    const [useGPTRevision, setUseGPTRevision] = useState(false);
    const [activeTab, setActiveTab] = useState('appointment');

    const [showImagePreview, setShowImagePreview] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);

    const [specialData, setSpecialData] = useState({});

    const maxFileSize = 50 * 1024 * 1024;

    const {
        selectedFiles,
        fileError,
        setFileError,
        existingFiles,
        filesToDelete,
        setSelectedFiles,
        setExistingFiles,
        setFilesToDelete,
        handleFileChange,
        removeFile,
        toggleDeleteFile,
    } = useAttachments({ editingNote, maxFileSize });

    const {
        treatments,
        prescriptionNumber,
        diagnosis,
        setDiagnosis,
        height,
        weight,
        loadingTreatments,
        uniqueTreatments,
        isLoadingUniqueTreatments,
        showAdvancedOptions,
        setShowAdvancedOptions,
        openDropdownIndex,
        setOpenDropdownIndex,
        treatmentRefs,
        handleAddTreatment,
        handleRemoveTreatment,
        handleTreatmentChange,
    } = usePrescriptionData({ show, editingNote });

    const {
        referrals,
        setReferrals,
        referralTo,
        setReferralTo,
        referralContent,
        setReferralContent,
        loadingReferrals,
        savingReferral,
        editingReferralId,
        setEditingReferralId,
        referralsToDelete,
        uniqueReferralTos,
        isLoadingUniqueReferralTos,
        isReferralDropdownOpen,
        setIsReferralDropdownOpen,
        referralWrapperRef,
        handleSaveReferral,
        handleEditReferral,
        handleCancelEditReferral,
        handleDeleteReferral,
    } = useReferralsData({ show, editingNote });

    const CurrentSpecialForm = noteFormRegistry[String(doctorFieldId || '')] || null;
    const isSpecialFormActive = Boolean(CurrentSpecialForm && userInfo?.doctor?.doctor_id === 29);

    const isNoteDateInFuture = () => isDateInFuture(noteForm.date);
    const isNoteDateToday = () => isDateToday(noteForm.date);

    const canAddReferralNow = () => {
        return !isAssistant && ((!editingNote && isNoteDateToday()) || (editingNote && !isNoteDateInFuture()));
    };

    const getPendingReferral = () => {
        const to = (referralTo || '').trim();
        const content = (referralContent || '').trim();
        if (!to || !content) return null;
        return { to, content };
    };

    const applyPendingReferralToList = (list) => {
        const pending = getPendingReferral();
        if (!canAddReferralNow() || !pending) return list;

        const base = Array.isArray(list) ? [...list] : [];

        if (editingReferralId) {
            const idx = base.findIndex(r => r.id === editingReferralId);
            if (idx >= 0) {
                base[idx] = { ...base[idx], ...pending };
                return base;
            }
        }

        base.unshift({ id: null, ...pending });
        return base;
    };

    const handlePreview = (file) => {
        setPreviewFile(file);
        setShowImagePreview(true);
    };

    const handleClosePreview = () => {
        setShowImagePreview(false);
        setPreviewFile(null);
    };

    const autosaveKey = getAutosaveKey({ editingNote, patientData, date: noteForm.date });

    useEffect(() => {
        if (show) {
            const savedDescription = loadFromLocalStorage({ key: autosaveKey });
            if (savedDescription) {
                const syntheticEvent = { target: { name: 'description', value: savedDescription } };
                onInputChange(syntheticEvent);
            }
        }
    }, [show, autosaveKey, onInputChange]);

    useEffect(() => {
        if (show && CurrentSpecialForm) {
            if (editingNote && editingNote.description) {
                try {
                    const parsed = typeof editingNote.description === 'string'
                        ? JSON.parse(editingNote.description)
                        : editingNote.description;

                    if (parsed && typeof parsed === 'object') {
                        const normalized = { ...parsed };
                        if (normalized.anc_gyne_category && !normalized.category) {
                            normalized.category = normalized.anc_gyne_category;
                        }
                        setSpecialData(normalized);
                    } else {
                        setSpecialData({});
                    }
                } catch (e) {
                    setSpecialData({});
                }
            } else {
                setSpecialData({});
            }
        } else if (show && !CurrentSpecialForm) {
            setSpecialData({});
        }
    }, [show, editingNote, CurrentSpecialForm]);

    useOutsideClick({
        enabled: openDropdownIndex !== null,
        getContainer: () => treatmentRefs.current[openDropdownIndex],
        onOutside: () => setOpenDropdownIndex(null),
    });

    useOutsideClick({
        enabled: isReferralDropdownOpen,
        getContainer: () => referralWrapperRef.current,
        onOutside: () => setIsReferralDropdownOpen(false),
    });

    useEffect(() => {
        if (show) {
            if (editingNote) {
                setActiveTab('clinical');
            } else {
                setActiveTab('appointment');
            }
        }
    }, [show, editingNote]);

    const hasContentThatPreventsDateChange = () => {
        if (!editingNote) return false;

        const hasDescription = editingNote.description && editingNote.description.trim() !== '';
        const hasMedia = existingFiles && existingFiles.length > 0;
        const hasPrescription = (treatments && treatments.some(t => t.trim() !== ''))
            || (diagnosis && diagnosis.trim() !== '')
            || (prescriptionNumber && prescriptionNumber.trim() !== '');

        return hasDescription || hasMedia || hasPrescription;
    };

    const handleDescriptionChange = (content) => {
        const syntheticEvent = { target: { name: 'description', value: content } };
        onInputChange(syntheticEvent);
        saveToLocalStorage({ key: autosaveKey, value: content });
    };

    const handleDateChange = (e) => {
        const newDate = e.target.value;

        if (editingNote && hasContentThatPreventsDateChange()) {
            const newDateObj = new Date(newDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            newDateObj.setHours(0, 0, 0, 0);

            if (newDateObj > today) {
                setFileError('Cannot change the date to a future date for notes that contain clinical notes, prescriptions, or media attachments.');
                return;
            }
        }

        setFileError('');
        onInputChange(e);
    };

    const handleEditReferralAndOpenTab = (ref) => {
        handleEditReferral(ref);
        setActiveTab('extra');
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const formData = new FormData();
        const payloadNoteForm = { ...noteForm };

        if (isSpecialFormActive) {
            try {
                payloadNoteForm.description = JSON.stringify(specialData || {});
            } catch (e2) {
                payloadNoteForm.description = '{}';
            }
        }

        Object.keys(payloadNoteForm).forEach(key => {
            let value = payloadNoteForm[key];
            if (key === 'description' || key === 'appointment_note') {
                value = value || '';
            }
            formData.append(key, value);
        });

        if (!editingNote) {
            formData.set('type', 'appointment');
        }

        try {
            const normalizedReferrals = Array.isArray(referrals)
                ? referrals.map(r => ({ id: r.id ?? null, to: r.to ?? '', content: r.content ?? '' }))
                : [];

            const baseReferrals = applyPendingReferralToList(normalizedReferrals);
            const pending = getPendingReferral();
            if (canAddReferralNow() && pending) {
                setReferrals(baseReferrals);
                setReferralTo('');
                setReferralContent('');
                setEditingReferralId(null);
            }

            formData.set('referrals', JSON.stringify(baseReferrals));
            const safeToDelete = Array.isArray(referralsToDelete) ? referralsToDelete : [];
            formData.set('referrals_to_delete', JSON.stringify(safeToDelete));
        } catch (err) {
            console.warn('Failed to serialize referrals for submission:', err);
        }

        if ((noteForm.type === 'treatment' || noteForm.type === 'appointment') || (!editingNote && isNoteDateToday())) {
            formData.append('number', prescriptionNumber);
            formData.append('diagnosis', diagnosis);
            if (height) formData.append('height', height);
            if (weight) formData.append('weight', weight);
            formData.append('treatments', JSON.stringify(treatments.filter(t => t.trim() !== '')));
        }

        selectedFiles.forEach((file) => {
            if (file instanceof File) {
                formData.append('files[]', file);
            } else if (file.file) {
                formData.append('files[]', file.file);
            }
        });

        if (editingNote && filesToDelete.length > 0) {
            formData.append('files_to_delete', JSON.stringify(filesToDelete));
        }

        console.log('FormData entries:');
        for (let pair of formData.entries()) {
            console.log(pair[0], pair[1]);
        }

        onSubmit(e, formData, useGPTRevision);

        clearFromLocalStorage({ key: autosaveKey });

        if (!editingNote) {
            setSelectedFiles([]);
            setExistingFiles([]);
            setFilesToDelete([]);
        }
    };

    return (
        <>
            <Modal show={show} onHide={onHide} size="lg">
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>{editingNote ? 'Edit Visit' : 'Add New Appointment'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {submitError && (
                            <Alert variant="danger" className="mb-3">
                                <i className="fas fa-exclamation-circle me-2"></i>
                                {submitError}
                            </Alert>
                        )}

                        <Tabs
                            id="note-tabs"
                            activeKey={activeTab}
                            onSelect={(k) => setActiveTab(k || 'appointment')}
                            className="mb-3"
                        >
                            <Tab eventKey="appointment" title="Appointment">
                                <AppointmentTab
                                    editingNote={editingNote}
                                    noteForm={noteForm}
                                    onInputChange={onInputChange}
                                    handleDateChange={handleDateChange}
                                    hasContentThatPreventsDateChange={hasContentThatPreventsDateChange}
                                />
                            </Tab>

                            <Tab eventKey="clinical" title="Clinical Notes">
                                <ClinicalTab
                                    editingNote={editingNote}
                                    noteForm={noteForm}
                                    isAssistant={isAssistant}
                                    isNoteDateToday={isNoteDateToday}
                                    isNoteDateInFuture={isNoteDateInFuture}
                                    CurrentSpecialForm={CurrentSpecialForm}
                                    isSpecialFormActive={isSpecialFormActive}
                                    specialData={specialData}
                                    setSpecialData={setSpecialData}
                                    handleDescriptionChange={handleDescriptionChange}
                                    useGPTRevision={useGPTRevision}
                                    setUseGPTRevision={setUseGPTRevision}
                                    existingFiles={existingFiles}
                                    filesToDelete={filesToDelete}
                                    handlePreview={handlePreview}
                                    toggleDeleteFile={toggleDeleteFile}
                                    selectedFiles={selectedFiles}
                                    submitting={submitting}
                                    handleFileChange={handleFileChange}
                                    removeFile={removeFile}
                                    fileError={fileError}
                                />
                            </Tab>

                            <Tab eventKey="prescription" title="Prescription">
                                <PrescriptionTab
                                    editingNote={editingNote}
                                    noteForm={noteForm}
                                    isAssistant={isAssistant}
                                    isNoteDateToday={isNoteDateToday}
                                    isNoteDateInFuture={isNoteDateInFuture}
                                    showAdvancedOptions={showAdvancedOptions}
                                    setShowAdvancedOptions={setShowAdvancedOptions}
                                    diagnosis={diagnosis}
                                    setDiagnosis={setDiagnosis}
                                    treatments={treatments}
                                    isLoadingUniqueTreatments={isLoadingUniqueTreatments}
                                    uniqueTreatments={uniqueTreatments}
                                    loadingTreatments={loadingTreatments}
                                    openDropdownIndex={openDropdownIndex}
                                    setOpenDropdownIndex={setOpenDropdownIndex}
                                    treatmentRefs={treatmentRefs}
                                    handleAddTreatment={handleAddTreatment}
                                    handleRemoveTreatment={handleRemoveTreatment}
                                    handleTreatmentChange={handleTreatmentChange}
                                />
                            </Tab>

                            <Tab eventKey="extra" title="Extra">
                                <ExtraTab
                                    noteForm={noteForm}
                                    onInputChange={onInputChange}
                                    isObgynDoctor={isObgynDoctor}
                                    isAssistant={isAssistant}
                                    editingNote={editingNote}
                                    isNoteDateToday={isNoteDateToday}
                                    isNoteDateInFuture={isNoteDateInFuture}
                                    referralWrapperRef={referralWrapperRef}
                                    referralTo={referralTo}
                                    setReferralTo={setReferralTo}
                                    referralContent={referralContent}
                                    setReferralContent={setReferralContent}
                                    uniqueReferralTos={uniqueReferralTos}
                                    isLoadingUniqueReferralTos={isLoadingUniqueReferralTos}
                                    isReferralDropdownOpen={isReferralDropdownOpen}
                                    setIsReferralDropdownOpen={setIsReferralDropdownOpen}
                                    savingReferral={savingReferral}
                                    editingReferralId={editingReferralId}
                                    handleSaveReferral={handleSaveReferral}
                                    handleCancelEditReferral={handleCancelEditReferral}
                                    loadingReferrals={loadingReferrals}
                                    referrals={referrals}
                                    handleEditReferral={handleEditReferralAndOpenTab}
                                    handleDeleteReferral={handleDeleteReferral}
                                />
                            </Tab>
                        </Tabs>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={onHide} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Saving...
                                </>
                            ) : 'Save'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <ImagePreviewModal show={showImagePreview} onHide={handleClosePreview} file={previewFile} />
        </>
    );
};

export default NoteModal;
